import clsx from 'clsx';
import { FC, MouseEvent, SyntheticEvent, useState } from 'react';

import AssignmentSwitch from 'src/components/assignments/assignment-switch';
import StreamCard from 'src/components/assignments/stream-card';
import { CARD_REMOVAL_DURATION } from 'src/constants/animation.constants';
import { LOWER_GRADES } from 'src/constants/grades.constants';
import {
  assignmentStudentHomeSections,
  msAssignmentStatuses,
} from 'src/constants/xApi.constants';
import useAssignmentToggle from 'src/hooks/use-assignment-toggle.hook';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import {
  AssignmentColorProps,
  AssignmentStatus,
  AssignmentsViewOptions,
  DesmosAssignment,
  MSAssignmentsStatusOptions,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { GRADES } from 'src/interfaces/grades.types';
import eventService from 'src/services/event.service';
import {
  useActiveMSAssignmentsStatus,
  useCurrentGradeSelector,
} from 'src/store/features';
import {
  getAssignmentContent,
  getIsAssignmentDueDateExpired,
} from 'src/utils/assignments.utils';
import { generateAssignmentDataForEventPayload } from 'src/utils/xapi.utils';

import './assignment-item.scss';

export interface AssignmentItemProps {
  assignment: SingleAssignment | DesmosAssignment;
  color: AssignmentColorProps;
  grade: GRADES;
  isMathAssessmentCard?: boolean;
  k5Section?: AssignmentsViewOptions.TODO | AssignmentsViewOptions.PAST_WORK;
}

const AssignmentItem: FC<AssignmentItemProps> = ({
  color,
  assignment,
  grade,
  isMathAssessmentCard = false,
  k5Section,
}) => {
  const [
    isCardRemovalAnimationInProgress,
    setIsCardRemovalAnimationInProgress,
  ] = useState(false);
  const [isStatusChangeDialogVisible, setIsStatusChangeDialogVisible] =
    useState(false);

  const { status, toggleStatus } = useAssignmentToggle(assignment);
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();
  const userCurrentGrade = useCurrentGradeSelector();
  const activeAssignmentsFilter = useActiveMSAssignmentsStatus();

  const { isHandedInmCLASSAssessment } = assignment;
  const isChecked = status === AssignmentStatus.READ;

  const isAssignmentDueDateExpired = getIsAssignmentDueDateExpired(assignment);
  const isMiddleSchoolView = useIsMiddleSchoolView();
  const isK1Grade = LOWER_GRADES.includes(grade);

  const isTodoSectionActive = k5Section === AssignmentsViewOptions.TODO;
  const isPastWorkSectionActive =
    k5Section === AssignmentsViewOptions.PAST_WORK;

  const shouldHideCheckMark =
    (!isChecked && isAssignmentDueDateExpired && !isMathAssessmentCard) ||
    isMiddleSchoolView ||
    (isK1Grade && !isMathAssessmentCard) ||
    isPastWorkSectionActive;

  const assignmentSwitchIconName = isStatusChangeDialogVisible
    ? 'icon-k5-close'
    : 'icon-k5-checkmark';

  const shouldShowContinueMsButton =
    activeAssignmentsFilter === null
      ? MSAssignmentsStatusOptions.STARTED.includes(assignment.status)
      : activeAssignmentsFilter === MSAssignmentsStatusOptions.STARTED;
  const MSAssignmentStatus = shouldShowContinueMsButton
    ? msAssignmentStatuses[MSAssignmentsStatusOptions.STARTED]
    : msAssignmentStatuses[MSAssignmentsStatusOptions.NOT_STARTED];
  const elementaryAssignmentStatus = isTodoSectionActive
    ? assignmentStudentHomeSections[AssignmentsViewOptions.TODO]
    : assignmentStudentHomeSections[AssignmentsViewOptions.PAST_WORK];

  const assignmentDataForEventPayload = generateAssignmentDataForEventPayload({
    assignmentBusinessKey: assignment.assignmentBk,
    assignmentClassBk: assignment.assignmentClassBks,
    classGrade: userCurrentGrade,
    content: getAssignmentContent(assignment),
    assignmentStatus: isMiddleSchoolView
      ? MSAssignmentStatus
      : elementaryAssignmentStatus,
    isAssignmentMarkedDone: isMiddleSchoolView ? undefined : isChecked,
  });

  const onAssignmentClick = (
    assignmentLink: string,
    e: MouseEvent<HTMLElement>,
  ) => {
    return eventService.send(
      XApiEventKeys.openedAssignment,
      {
        result: {
          destinationUrl: assignmentLink,
        },
      },
      {
        result: {
          isRightClick: e.type === 'contextmenu',
          ...commonXapiContextPayload,
          ...assignmentDataForEventPayload,
        },
      },
    );
  };

  const updateStatus = (assignmentLink: string, e: SyntheticEvent) => {
    e.preventDefault(); // prevent link click

    eventService.send(
      !isChecked
        ? XApiEventKeys.assignmentMarkedDone
        : XApiEventKeys.assignmentMarkedToDo,
      {
        result: {
          destinationUrl: assignmentLink,
          assignmentStatus: !isChecked ? 'done' : 'mark-as-to-do',
        },
      },
      {
        result: {
          isRightClick: e.type === 'contextmenu',
          ...commonXapiContextPayload,
          ...assignmentDataForEventPayload,
        },
      },
    );

    if (e.type !== 'contextmenu') {
      setIsCardRemovalAnimationInProgress(true);

      toggleStatus();

      // Wait until card removal animation is finished
      setTimeout(() => {
        setIsCardRemovalAnimationInProgress(false);
      }, CARD_REMOVAL_DURATION);
    }
  };

  const handleAssignmentSwitchClick = (e: SyntheticEvent) => {
    if (e.type === 'contextmenu') return;

    setIsStatusChangeDialogVisible((prev) => !prev);
  };

  return (
    <div
      className={clsx('assignment', {
        'assignment--hidden-hover':
          isMiddleSchoolView || isHandedInmCLASSAssessment,
        'assignment--to-do-k5': !isMiddleSchoolView && isTodoSectionActive,
        'assignment--checkmark-hidden':
          !isMiddleSchoolView && shouldHideCheckMark,
        'assignment--past-work': isPastWorkSectionActive,
      })}
    >
      {!shouldHideCheckMark && (
        <AssignmentSwitch
          isChecked={isChecked}
          onClick={handleAssignmentSwitchClick}
          onContextMenu={handleAssignmentSwitchClick}
          isAnimationInProgress={isCardRemovalAnimationInProgress}
          iconName={assignmentSwitchIconName}
        />
      )}
      <StreamCard
        assignment={assignment}
        assignmentDataForEventPayload={assignmentDataForEventPayload}
        onStreamLinkClick={onAssignmentClick}
        grade={grade}
        color={color}
        isStatusChangeDialogVisible={isStatusChangeDialogVisible}
        setIsStatusChangeDialogVisible={setIsStatusChangeDialogVisible}
        onAssignmentComplete={updateStatus}
        isCardRemovalAnimationInProgress={isCardRemovalAnimationInProgress}
        k5Section={k5Section}
      />
    </div>
  );
};

export default AssignmentItem;
